import React from "react";
import requests from "../Auth/Requests";
import { Box, Typography, Paper, List, ListItem, ListItemText, Button, Select, MenuItem, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, FormGroup, FormControlLabel, Checkbox
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { diff as deepDiff } from 'deep-diff';
import globals from '../Globals'

export default class DatabasePage extends React.Component {
    state = {
        versionData: [],
        selectedBranch: "",
        selectedVersion: null,
        selectedVersionId: null,
        infoPanel: null,
        addPatchDialogOpen: false,
        newPatchValue: "",
        selectLatestAfterAdd: false,
        showDownloadDialog: false,
        downloadResult: "",
        downloadTempId: null,
        selectedCategory: null,
        // Inspector dialog state
        selectedJsonVersion: null,
        diffJsonVersion: null,
        loadedGameDbVersions: [], // array of { version: string, json: string or object }
        loadedMetaDbVersions: [], // array of { version: string, json: string or object }
        loadingJsonVersion: false,
        inspectorReadOnly: false,
        // New Load Version dialog state
        loadDialogOpen: false,
        loadBranch: "",
        loadMajor: 0,
        loadMinor: 0,
        loadPatch: 0,
        metaDb: [],
        // For client release checkboxes
        releaseSelections: 0,
        releaseChanged: false,
        // For server deploy dialog
        deployDialogOpen: false,
        deployFile: null,
        deploying: false,
    };

    componentDidMount() {
        const parts = window.location.hash.split("-");
        const maybeBranch = parts[2];
        const maybeVersion = parts[3];
        const maybeType = parts[4];
        const maybePatch = parts[5];

        let selectedBranch = "";
        let selectedVersion = null;
        let selectedVersionId = null;
        let infoPanel = null;

        if (maybeBranch && globals.branches.includes(maybeBranch)) {
            selectedBranch = maybeBranch;
        }

        if (maybeVersion && !isNaN(maybeVersion)) {
            selectedVersion = Number(maybeVersion);
        }

        // Only set infoPanel if both type and patch are present and valid
        const validTypes = ["client", "server", "gameDb", "metaDb"];
        if (maybeType && validTypes.includes(maybeType) && maybePatch && !isNaN(maybePatch)) {
            // For client, keep as { type, patch } for now (releases will be filled after data load)
            infoPanel = { type: maybeType, patch: Number(maybePatch) };
        }

        this.setState({ selectedBranch, selectedVersion, selectedVersionId, infoPanel }, () => {
            this.performAction("Loading", "list");
        });
    }

    performAction(description, ...args) {
        const cmd = args.join(" ");
        const key = enqueueSnackbar(`${description}...`, { variant: 'info', persist: true });

        requests.request("versioning " + cmd, {})
            .then(response => {
                const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
                const versions = response.find(d => d.branch === branchIndex)?.versions ?? [];
                const latest = versions.length > 0 ? versions[0] : null;
                if (this.state.selectLatestAfterAdd && description === "Add version") {
                    this.setState({
                        versionData: response,
                        selectedVersion: latest?.version ?? null,
                        selectedVersionId: latest?.id ?? null,
                        selectLatestAfterAdd: false
                    }, () => {
                        this.updateHash();
                        closeSnackbar(key);
                        enqueueSnackbar(`${description} successful!`, { variant: 'success' });
                    });
                } else {
                    // Compute new selectedVersionId
                    const newSelectedVersionId = (() => {
                        if (this.state.selectedVersion == null) return null;
                        const v = versions.find(v => v.version === this.state.selectedVersion);
                        return v?.id ?? null;
                    })();
                    // Prepare possible infoPanel update for client type
                    let infoPanelUpdate = null;
                    let releaseSelectionsUpdate = undefined;
                    let releaseChangedUpdate = undefined;
                    if (
                        this.state.infoPanel &&
                        this.state.infoPanel.type === "client"
                    ) {
                        // Find the correct client patch entry
                        const selectedBranchData = response.find(d => d.branch === branchIndex);
                        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === newSelectedVersionId);
                        if (selectedVersionData && Array.isArray(selectedVersionData.client)) {
                            const patchEntry = selectedVersionData.client.find(
                                p => p.patch === this.state.infoPanel.patch
                            );
                            if (patchEntry) {
                                infoPanelUpdate = {
                                    ...this.state.infoPanel,
                                    releases: patchEntry.released ?? 0
                                };
                                releaseSelectionsUpdate = patchEntry.released ?? 0;
                                releaseChangedUpdate = false;
                            }
                        }
                    }
                    this.setState(
                        {
                            versionData: response,
                            selectedVersionId: newSelectedVersionId,
                            ...(infoPanelUpdate ? { infoPanel: infoPanelUpdate } : {}),
                            ...(releaseSelectionsUpdate !== undefined ? { releaseSelections: releaseSelectionsUpdate } : {}),
                            ...(releaseChangedUpdate !== undefined ? { releaseChanged: releaseChangedUpdate } : {})
                        },
                        () => {
                            closeSnackbar(key);
                            enqueueSnackbar(`${description} successful!`, { variant: 'success' });
                        }
                    );
                }
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} failed: ${error.message}`, { variant: 'error' });
            });
    }

    // General-purpose request with snackbar, returns the result (Promise)
    performRequest(description, ...args) {
        let payload = {};
        if (typeof args[0] === "object" && args[0] !== null) {
            payload = args[0];
            args = args.slice(1);
        }
        const cmd = args.join(" ");
        const key = enqueueSnackbar(`${description}...`, { variant: 'info', persist: true });

        return requests.request("versioning " + cmd, payload)
            .then(response => {
                if (Array.isArray(response)) {
                    closeSnackbar(key);
                    enqueueSnackbar(`${description} returned errors.`, { variant: 'error' });
                    return response;
                }
                closeSnackbar(key);
                enqueueSnackbar(`${description} successful!`, { variant: 'success' });
                return response;
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} failed: ${error.message}`, { variant: 'error' });
            });
    }

    handleBranchChange = (branch) => {
        const branchIndex = globals.branches.indexOf(branch);
        const branchData = this.state.versionData.find(d => d.branch === branchIndex);
        const latestVersionData = branchData?.versions[0];
        this.setState({
            selectedBranch: branch,
            selectedVersion: latestVersionData?.version ?? null,
            selectedVersionId: latestVersionData?.id ?? null,
            infoPanel: null
        }, this.updateHash);
    };

    handleVersionChange = (version) => {
        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
        const selectedVersions = this.state.versionData.find(v => v.branch === branchIndex)?.versions ?? [];
        if (version === "add-new") {
            this.setState({ selectLatestAfterAdd: true });
            this.performAction("Add version", "add", this.state.selectedBranch);
        } else {
            const versionData = selectedVersions.find(v => v.version === Number(version));
            this.setState({
                selectedVersion: Number(version),
                selectedVersionId: versionData?.id ?? null,
                infoPanel: null
            }, this.updateHash);
        }
    };

    updateHash = () => {
        // Only update hash for in-page selector/infoPanel changes, using replaceState
        const base = window.location.hash.split("-").slice(0, 2).join("-");
        const { selectedBranch, selectedVersion, infoPanel } = this.state;
        const branchParam = selectedBranch ? `-${selectedBranch}` : "";
        const versionParam = selectedVersion != null ? `-${selectedVersion}` : "";
        let typeParam = "";
        let patchParam = "";
        if (infoPanel && infoPanel.type && infoPanel.patch !== undefined && infoPanel.patch !== null) {
            typeParam = `-${infoPanel.type}`;
            patchParam = `-${infoPanel.patch}`;
        }
        const newHash = `${base}${branchParam}${versionParam}${typeParam}${patchParam}`;
        window.history.replaceState(null, "", newHash);
    };

    constructor(props) {
        super(props);
        this.state = {
            versionData: [],
            selectedBranch: "",
            selectedVersion: null,
            selectedVersionId: null,
            infoPanel: null,
            addPatchDialogOpen: false,
            newPatchValue: "",
            selectLatestAfterAdd: false,
            showDownloadDialog: false,
            downloadResult: "",
            downloadTempId: null,
            selectedCategory: null,
            selectedJsonVersion: null,
            diffJsonVersion: null,
            loadedGameDbVersions: [],
            loadedMetaDbVersions: [],
            loadingJsonVersion: false,
            inspectorReadOnly: false,
            loadDialogOpen: false,
            loadBranch: "",
            loadMajor: 0,
            loadMinor: 0,
            loadPatch: 0,
            // For server deploy dialog
            deployDialogOpen: false,
            deployFile: null,
            deploying: false,
        };
        this.handleAddClientPatch = this.handleAddClientPatch.bind(this);
        this.handleAddGameDb = this.handleAddGameDb.bind(this);
        this.handleAddMetaDb = this.handleAddMetaDb.bind(this);
    }
    // --- Server Deploy Dialog handlers ---
    handleOpenDeployDialog = () => {
        this.setState({ deployDialogOpen: true, deployFile: null, deploying: false });
    };

    handleCloseDeployDialog = () => {
        this.setState({ deployDialogOpen: false, deployFile: null, deploying: false });
    };

    handleFileChange = (e) => {
        const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        this.setState({ deployFile: file });
    };

    handleDeploy = async () => {
        const { deployFile } = this.state;
        if (!deployFile || !this.state.selectedVersionId) return;
        this.setState({ deploying: true });
        try {
            // Send the file as binary (not base64)
            await this.performRequest("Upload Server", deployFile, "upload-server", this.state.selectedVersionId);
            // Reload version info after successful upload
            this.performAction("Loading", "list");
            this.setState({ deployDialogOpen: false, deployFile: null, deploying: false });
        } catch (e) {
            this.setState({ deploying: false });
        }
    };

    // Helper: get string version from major/minor/patch
    versionString = (major, minor, patch) => `${major}.${minor}.${patch}`;

    addLoadedGameDbVersion = (versionStr, json, branch = this.state.selectedBranch) => {
        this.setState(prev => {
            const idx = prev.loadedGameDbVersions.findIndex(v => v.version === versionStr);
            const updated = [...prev.loadedGameDbVersions];
            const newEntry = { version: versionStr, json, branch };
            if (idx !== -1) {
                updated[idx] = newEntry;
            } else {
                updated.push(newEntry);
            }
            return { loadedGameDbVersions: updated };
        });
    };

    addLoadedMetaDbVersion = (versionStr, json, branch = this.state.selectedBranch) => {
        this.setState(prev => {
            const idx = prev.loadedMetaDbVersions.findIndex(v => v.version === versionStr);
            const updated = [...prev.loadedMetaDbVersions];
            const newEntry = { version: versionStr, json, branch };
            if (idx !== -1) {
                updated[idx] = newEntry;
            } else {
                updated.push(newEntry);
            }
            return { loadedMetaDbVersions: updated };
        });
    };

    // Open inspector dialog and initialize state
    handleAddGameDb() {
        this.performRequest("Download Game DB", "download-docs", "game")
            .then(result => {
                if (!this.state.selectedVersionId) {
                    enqueueSnackbar("Unable to locate selected version ID.", { variant: "error" });
                    return;
                }
                if (Array.isArray(result)) {
                    this.setState({
                        showDownloadDialog: true,
                        downloadResult: result,
                        downloadTempId: null,
                        selectedCategory: null,
                        selectedJsonVersion: null,
                        diffJsonVersion: null,
                        inspectorReadOnly: true,
                        downloadSource: "gameDb"
                    });
                    return;
                }
                // Parse version from selectedVersion
                const { branch, major, minor } = globals.decompactVersion(this.state.selectedVersion);
                // Find latest patch for database
                const selectedBranchData = this.state.versionData.find(d => d.branch === globals.branches.indexOf(this.state.selectedBranch));
                const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                const noPreviousVersion = !selectedVersionData?.gameDb || selectedVersionData.gameDb.length === 0;
                const latestPatch = selectedVersionData?.gameDb.reduce((max, p) => Math.max(max, p.patch), -1);
                const newPatch = latestPatch + 1;
                const compact = globals.compactVersion(major, minor, newPatch, branch);
                const versionStr = this.versionString(major, minor, newPatch);
                // Always add current version to loadedGameDbVersions
                const resultJson = result.data.json;
                this.addLoadedGameDbVersion(versionStr, resultJson, this.state.selectedBranch);
                this.setState({
                    showDownloadDialog: true,
                    downloadResult: resultJson,
                    downloadTempId: result.id,
                    selectedCategory: null,
                    selectedJsonVersion: versionStr,
                    diffJsonVersion: "None",
                    // loadedJsonVersions update removed
                    loadingJsonVersion: false,
                    inspectorReadOnly: false,
                    downloadSource: "gameDb"
                });
                if (noPreviousVersion) return;
                // Only fetch previous version if it exists
                const previousPatch = latestPatch;
                const previousCompact = globals.compactVersion(major, minor, previousPatch, branch);
                const previousVersionStr = this.versionString(major, minor, previousPatch);
                this.performRequest("Fetch current Game DB", "get-docs", "game", previousCompact)
                    .then(previous => {
                        const previousJson = previous.data.json;
                        this.addLoadedGameDbVersion(previousVersionStr, previousJson, this.state.selectedBranch);
                        this.setState({
                            diffJsonVersion: previousVersionStr
                        });
                    })
                    .catch(() => {
                        // If fetch fails, fallback to no diff
                        this.setState({ diffJsonVersion: "None" });
                    });
            })
            .catch(error => {
                // Snackbar already shown in performRequest
                this.setState({ downloadSource: "gameDb" });
            });
    }

    // Open inspector dialog and initialize state for Meta DB
    handleAddMetaDb() {
        this.performRequest("Download Meta DB", "download-docs", "meta")
            .then(result => {
                if (!this.state.selectedVersionId) {
                    enqueueSnackbar("Unable to locate selected version ID.", { variant: "error" });
                    return;
                }
                if (Array.isArray(result)) {
                    this.setState({
                        showDownloadDialog: true,
                        downloadResult: result,
                        downloadTempId: null,
                        selectedCategory: null,
                        selectedJsonVersion: null,
                        diffJsonVersion: null,
                        inspectorReadOnly: true,
                        downloadSource: "metaDb"
                    });
                    return;
                }
                // Parse version from selectedVersion
                const { branch, major, minor } = globals.decompactVersion(this.state.selectedVersion);
                // Find latest patch for database
                const selectedBranchData = this.state.versionData.find(d => d.branch === globals.branches.indexOf(this.state.selectedBranch));
                const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                const noPreviousVersion = !selectedVersionData?.metaDb || selectedVersionData.metaDb.length === 0;
                const latestPatch = selectedVersionData?.metaDb.reduce((max, p) => Math.max(max, p.patch), -1);
                const newPatch = latestPatch + 1;
                const compact = globals.compactVersion(major, minor, newPatch, branch);
                const versionStr = this.versionString(major, minor, newPatch);
                // Always add current version to loadedMetaDbVersions
                const resultJson = JSON.stringify(result.data);
                console.log(JSON.stringify(result));
                this.addLoadedMetaDbVersion(versionStr, resultJson, this.state.selectedBranch);
                this.setState({
                    showDownloadDialog: true,
                    downloadResult: resultJson,
                    downloadTempId: result.id,
                    selectedCategory: null,
                    selectedJsonVersion: versionStr,
                    diffJsonVersion: "None",
                    // loadedJsonVersions update removed
                    loadingJsonVersion: false,
                    inspectorReadOnly: false,
                    downloadSource: "metaDb"
                });
                if (noPreviousVersion) return;
                // Only fetch previous version if it exists
                const previousPatch = latestPatch;
                const previousCompact = globals.compactVersion(major, minor, previousPatch, branch);
                const previousVersionStr = this.versionString(major, minor, previousPatch);
                this.performRequest("Fetch current Meta DB", "get-docs", "meta", previousCompact)
                    .then(previous => {
                        const previousJson = JSON.stringify(previous.data);
                        this.addLoadedMetaDbVersion(previousVersionStr, previousJson, this.state.selectedBranch);
                        this.setState({
                            diffJsonVersion: previousVersionStr
                        });
                    })
                    .catch(() => {
                        // If fetch fails, fallback to no diff
                        this.setState({ diffJsonVersion: "None" });
                    });
            })
            .catch(error => {
                // Snackbar already shown in performRequest
                this.setState({ downloadSource: "metaDb" });
            });
    }

    handleAddClientPatch() {
        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
        const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
        if (!selectedVersionData) return;

        const nextPatch = selectedVersionData.client.length > 0
            ? Math.max(...selectedVersionData.client.map(p => p.patch)) + 1
            : 0;

        this.setState({ addPatchDialogOpen: true, newPatchValue: nextPatch.toString() });
    }

    handleConfirmAddPatch = () => {
        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
        const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
        if (!selectedVersionData) return;

        const patch = parseInt(this.state.newPatchValue);
        if (isNaN(patch)) return;

        const currentPatches = selectedVersionData.client.map(p => p.patch);
        const maxPatch = currentPatches.length > 0 ? Math.max(...currentPatches) : -1;
        if (patch <= maxPatch) {
            enqueueSnackbar("Patch must be greater than existing patches.", { variant: "warning" });
            return;
        }

        this.setState({ addPatchDialogOpen: false, newPatchValue: "" });
        this.performAction("Add client patch", "add-client", selectedVersionData.id.toString(), patch.toString());
    };

    handleCancelAddPatch = () => {
        this.setState({ addPatchDialogOpen: false, newPatchValue: "" });
    };

    renderList = (label, items, type, showAdd = false, onAdd = null) => (
        <Paper sx={{ flex: 1, marginX: 0.5 }}>
            <Typography variant="h6" sx={{ padding: 1 }}>{label}</Typography>
            <Box sx={{ maxHeight: 232, overflowY: 'auto', display: 'flex', flexDirection: 'column-reverse' }}>
                <List dense>
                    {items.map((entry, index) => (
                        type === "client" ? (
                            <ListItem
                                key={index}
                                button
                                onClick={() => this.setState({
                                    infoPanel: { type, patch: entry.patch, releases: entry.released ?? 0 },
                                    releaseSelections: entry.released ?? 0,
                                    releaseChanged: false,
                                }, this.updateHash)}
                            >
                                <ListItemText
                                    primary={
                                        this.state.selectedVersion != null
                                            ? (() => {
                                                const patch = entry.patch;
                                                const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                return `${major}.${minor}.${patch}`;
                                            })()
                                            : `Patch ${entry.patch}`
                                    }
                                />
                            </ListItem>
                        ) : (
                            <ListItem
                                key={index}
                                button
                                onClick={() => this.setState(
                                    { infoPanel: { type, patch: entry.patch } },
                                    this.updateHash
                                )}
                            >
                                <ListItemText
                                    primary={
                                        this.state.selectedVersion != null
                                            ? (() => {
                                                const patch = entry.patch;
                                                const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                return `${major}.${minor}.${patch}`;
                                            })()
                                            : `Patch ${entry.patch}`
                                    }
                                />
                            </ListItem>
                        )
                    ))}
                    {/* Add deploy button for Server list */}
                    {label === "Server" && (
                        <ListItem disablePadding>
                            <Button fullWidth onClick={this.handleOpenDeployDialog} sx={{ justifyContent: 'flex-start', padding: '6px 16px' }}>
                                + DEPLOY
                            </Button>
                        </ListItem>
                    )}
                    {onAdd && (
                        <ListItem disablePadding>
                            <Button fullWidth onClick={onAdd} sx={{ justifyContent: 'flex-start', padding: '6px 16px' }}>
                                + Import
                            </Button>
                        </ListItem>
                    )}
                    {!onAdd && showAdd && (
                        <ListItem disablePadding>
                            <Button fullWidth onClick={this.handleAddClientPatch} sx={{ justifyContent: 'flex-start', padding: '6px 16px' }}>
                                + Register
                            </Button>
                        </ListItem>
                    )}
                </List>
            </Box>
        </Paper>
    );

    componentWillUnmount() {
        if (this.state.showDownloadDialog && this.state.downloadTempId && this.state.downloadSource) {
            this.performAction(
                "Cancel Docs",
                "cancel-docs",
                this.state.downloadTempId
            );
        }
    }

    render() {
        const { versionData, selectedBranch, selectedVersion, selectedVersionId, infoPanel } = this.state;
        const branchIndex = globals.branches.indexOf(selectedBranch);
        const selectedVersions = versionData.find(v => v.branch === branchIndex)?.versions ?? [];
        const selectedData = selectedVersions.find(v => v.id === selectedVersionId);

        // --- Download Dialog Table logic with new state ---
        const loadedVersions = this.state.downloadSource === "metaDb"
            ? this.state.loadedMetaDbVersions
            : this.state.loadedGameDbVersions;
        let sourceJson = "{}";
        let baseJson = null;
        let showDiff = false;
        let categoryOptions = [];
        let parsedJson = {};
        let tableRows = [], tableColumns = [];
        let diffResult = [];
        let selectedCategory = this.state.selectedCategory;
        if (this.state.showDownloadDialog) {
            // If downloadResult is an error array, skip further parsing for table
            if (!Array.isArray(this.state.downloadResult)) {
                // Find source and diff JSONs
                sourceJson = (() => {
                    const found = loadedVersions.find(v => v.version === this.state.selectedJsonVersion);
                    if (!found) return "{}";
                    return typeof found.json === "string" ? found.json : JSON.stringify(found.json);
                })();
                baseJson = (() => {
                    if (!this.state.diffJsonVersion || this.state.diffJsonVersion === "None") return null;
                    const found = loadedVersions.find(v => v.version === this.state.diffJsonVersion);
                    if (!found) return null;
                    return typeof found.json === "string" ? found.json : JSON.stringify(found.json);
                })();
                showDiff = !!(this.state.diffJsonVersion && this.state.diffJsonVersion !== "None" && this.state.diffJsonVersion !== this.state.selectedJsonVersion);
                try {
                    parsedJson = JSON.parse(sourceJson);
                    categoryOptions = showDiff ? ["All", ...Object.keys(parsedJson)] : Object.keys(parsedJson);
                } catch (e) {
                    parsedJson = {};
                    categoryOptions = [];
                }
                // Compute diffResult if in diff mode
                if (showDiff) {
                    try {
                        const oldData = baseJson ? JSON.parse(baseJson) : {};
                        const newData = JSON.parse(sourceJson);
                        diffResult = deepDiff(oldData, newData) || [];
                    } catch (e) {}
                }
                selectedCategory = categoryOptions.includes(this.state.selectedCategory)
                    ? this.state.selectedCategory
                    : (showDiff ? "All" : categoryOptions[0]);
                const selectedItems = parsedJson[selectedCategory] || {};
                const uniqueKeys = [...new Set(Object.values(selectedItems).flatMap(obj => Object.keys(obj)))];
                tableColumns = [{ field: 'id', headerName: 'id', flex: 1 }, ...uniqueKeys.map(key => ({ field: key, headerName: key, flex: 1 }))];
                tableRows = Object.entries(selectedItems).map(([id, values], idx) => ({ id, ...values }));
            }
        }

        return (
            <Box sx={{ padding: 3 }}>
                <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
                    <Select value={selectedBranch} onChange={(e) => this.handleBranchChange(e.target.value)} displayEmpty sx={{ minWidth: 200, backgroundColor: 'white' }}>
                        <MenuItem disabled value="">Select Branch</MenuItem>
                        {globals.branches.map(branch => (
                            <MenuItem key={branch} value={branch}>{branch}</MenuItem>
                        ))}
                    </Select>
                    {selectedBranch && (
                        <Select value={selectedVersion ?? ""} onChange={(e) => this.handleVersionChange(e.target.value)} displayEmpty sx={{ minWidth: 200, backgroundColor: 'white' }}>
                            <MenuItem value="add-new">+ Add New</MenuItem>
                            {selectedVersions.map(v => {
                                const { major, minor } = globals.decompactVersion(v.version);
                                return (
                                    <MenuItem key={v.id} value={v.version}>{major}.{minor}.0</MenuItem>
                                );
                            })}
                        </Select>
                    )}
                </Box>
                {selectedData && (
                    <>
                        <Grid container spacing={1.5}>
                            {this.renderList("Client", selectedData.client, "client", true)}
                            {this.renderList("Server", selectedData.server, "server")}
                            {this.renderList("Game DB", selectedData.gameDb, "gameDb", false, this.handleAddGameDb)}
                            {this.renderList("Meta DB", selectedData.metaDb, "metaDb", false, this.handleAddMetaDb)}
                        </Grid>
                        {/* --- Server Deploy Dialog --- */}
                        <Dialog
                            open={this.state.deployDialogOpen}
                            onClose={this.handleCloseDeployDialog}
                        >
                            <DialogTitle>Deploy Server Patch (.zip)</DialogTitle>
                            <DialogContent>
                                <Box sx={{ mt: 1 }}>
                                    <input
                                        type="file"
                                        accept=".zip"
                                        onChange={this.handleFileChange}
                                        disabled={this.state.deploying}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.handleCloseDeployDialog} disabled={this.state.deploying}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={this.handleDeploy}
                                    disabled={!this.state.deployFile || this.state.deploying}
                                >
                                    {this.state.deploying ? "Deploying..." : "Deploy"}
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Box sx={{ marginTop: 3 }}>
                            {infoPanel && (
                                <Paper sx={{ padding: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">
                                            {(() => {
                                                const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                return `${infoPanel.type.charAt(0).toUpperCase() + infoPanel.type.slice(1)} ${major}.${minor}.${infoPanel.patch}`;
                                            })()}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                            Created: {
                                                (() => {
                                                    const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
                                                    const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
                                                    const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                                                    if (!selectedVersionData) return "Unknown";

                                                    const patchLists = {
                                                        client: selectedVersionData.client,
                                                        server: selectedVersionData.server,
                                                        gameDb: selectedVersionData.gameDb,
                                                        metaDb: selectedVersionData.metaDb
                                                    };

                                                    const entry = patchLists[infoPanel.type].find(p => p.patch === infoPanel.patch);
                                                    if (!entry || !entry.createdDate || entry.createdDate.startsWith("0001-01-01")) return "Unknown";

                                                    const date = new Date(entry.createdDate);
                                                    return date.toLocaleString();
                                                })()
                                            }
                                        </Typography>
                                        {infoPanel.type === "gameDb" ? (
                                            <Button
                                              variant="outlined"
                                              onClick={() => {
                                                const selectedBranchData = this.state.versionData.find(d => d.branch === globals.branches.indexOf(this.state.selectedBranch));
                                                const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                                                if (!selectedVersionData) return;

                                                // Compute compact version for get-gamedb
                                                const { branch, major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                const compact = globals.compactVersion(major, minor, infoPanel.patch, branch);

                                                this.performRequest("Fetch selected Game DB", "get-docs", "game", compact)
                                                    .then(result => {
                                                        const versionStr = this.versionString(major, minor, infoPanel.patch);
                                                        const resultJson = result.data.json;
                                                        this.addLoadedGameDbVersion(versionStr, resultJson, this.state.selectedBranch);
                                                        this.setState({
                                                            showDownloadDialog: true,
                                                            downloadResult: resultJson,
                                                            selectedCategory: null,
                                                            selectedJsonVersion: versionStr,
                                                            diffJsonVersion: "None",
                                                            inspectorReadOnly: true,
                                                            downloadSource: "gameDb"
                                                        });
                                                    });
                                              }}
                                            >
                                              View
                                            </Button>
                                        ) : infoPanel.type === "metaDb" ? (
                                            <Button
                                              variant="outlined"
                                              onClick={() => {
                                                const selectedBranchData = this.state.versionData.find(d => d.branch === globals.branches.indexOf(this.state.selectedBranch));
                                                const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                                                if (!selectedVersionData) return;

                                                // Compute compact version for get-metadb
                                                const { branch, major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                const compact = globals.compactVersion(major, minor, infoPanel.patch, branch);

                                                this.performRequest("Fetch selected Meta DB", "get-docs", "meta", compact)
                                                    .then(result => {
                                                        const versionStr = this.versionString(major, minor, infoPanel.patch);
                                                        const resultJson = JSON.stringify(result.data)
                                                        this.addLoadedMetaDbVersion(versionStr, resultJson, this.state.selectedBranch);
                                                        this.setState({
                                                            showDownloadDialog: true,
                                                            downloadResult: resultJson,
                                                            selectedCategory: null,
                                                            selectedJsonVersion: versionStr,
                                                            diffJsonVersion: "None",
                                                            inspectorReadOnly: true,
                                                            downloadSource: "metaDb"
                                                        });
                                                    });
                                              }}
                                            >
                                              View
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                disabled={
                                                    (() => {
                                                        const branchIndex = globals.branches.indexOf(this.state.selectedBranch);
                                                        const selectedBranchData = this.state.versionData.find(d => d.branch === branchIndex);
                                                        const selectedVersionData = selectedBranchData?.versions.find(v => v.id === this.state.selectedVersionId);
                                                        if (!selectedVersionData) return true;

                                                        const patchLists = {
                                                            client: selectedVersionData.client,
                                                            server: selectedVersionData.server,
                                                            gameDb: selectedVersionData.gameDb,
                                                            metaDb: selectedVersionData.metaDb
                                                        };

                                                        const patches = patchLists[infoPanel.type].map(p => p.patch);
                                                        return patches.length === 0 || infoPanel.patch !== Math.max(...patches);
                                                    })()
                                                }
                                                onClick={() => {
                                                    if (!this.state.selectedVersionId) return;
                                                    if (infoPanel.type === "client") {
                                                        this.performAction("Delete client patch", "delete-client", this.state.selectedVersionId.toString(), infoPanel.patch.toString());
                                                    } else if (infoPanel.type === "server") {
                                                        // TODO: implement delete-server
                                                    } else if (infoPanel.type === "gameDb") {
                                                        // TODO: implement delete-gameDb
                                                    } else if (infoPanel.type === "metaDb") {
                                                        // TODO: implement delete-metaDb
                                                    }
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </Box>
                                    {/* Release platform checkboxes for client type */}
                                    {infoPanel.type === "client" && (
                                        <Box sx={{ mt: 2 }}>
                                            {/* Platform checkboxes */}
                                            <FormGroup row>
                                                {globals.platformNames.map((platform, idx) => (
                                                    <FormControlLabel
                                                        key={platform}
                                                        control={
                                                            <Checkbox
                                                                checked={(this.state.releaseSelections & (1 << idx)) !== 0}
                                                                onChange={(e) => {
                                                                    const bit = 1 << idx;
                                                                    const newValue = e.target.checked
                                                                        ? this.state.releaseSelections | bit
                                                                        : this.state.releaseSelections & ~bit;
                                                                    this.setState({
                                                                        releaseSelections: newValue,
                                                                        releaseChanged: newValue !== (this.state.infoPanel.releases ?? 0)
                                                                    });
                                                                }}
                                                            />
                                                        }
                                                        label={platform}
                                                    />
                                                ))}
                                            </FormGroup>
                                            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    disabled={!this.state.releaseChanged}
                                                    sx={{ mt: 1 }}
                                                    onClick={() => {
                                                        const versionId = this.state.selectedVersionId;
                                                        const patch = this.state.infoPanel.patch;
                                                        const flags = this.state.releaseSelections;
                                                        this.performAction("Update client release", "update-client-release", versionId, patch, flags);
                                                        this.setState({
                                                            releaseChanged: false,
                                                            infoPanel: { ...this.state.infoPanel, releases: flags }
                                                        });
                                                    }}
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </Paper>
                            )}
                        </Box>
                        <Dialog open={this.state.addPatchDialogOpen} onClose={this.handleCancelAddPatch}>
                            <DialogTitle>Add New Client Patch</DialogTitle>
                            <DialogContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography>
                                        {(() => {
                                            const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                            return `${major}.${minor}.`;
                                        })()}
                                    </Typography>
                                    <TextField
                                        type="number"
                                        variant="standard"
                                        value={this.state.newPatchValue}
                                        onChange={(e) => this.setState({ newPatchValue: e.target.value })}
                                        autoFocus
                                        inputProps={{ min: 0, style: { width: '60px' } }}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.handleCancelAddPatch}>Cancel</Button>
                                <Button variant="contained" onClick={this.handleConfirmAddPatch}>Add</Button>
                            </DialogActions>
                        </Dialog>
                        <Dialog
                            open={this.state.showDownloadDialog}
                            onClose={(e, reason) => {
                                if (reason !== 'backdropClick') {
                                    this.setState({ showDownloadDialog: false });
                                }
                            }}
                            maxWidth="lg"
                            fullWidth
                            scroll="paper"
                        >
                            <DialogTitle>Inspector</DialogTitle>
                            <DialogContent>
                                {/* Error list rendering for Game DB errors */}
                                {Array.isArray(this.state.downloadResult) ? (
                                    <Box sx={{ padding: 2 }}>
                                        <Typography variant="h6" gutterBottom>Sheets contain errors:</Typography>
                                        <List>
                                            {this.state.downloadResult.map((err, i) => (
                                                <ListItem key={i}>
                                                    <ListItemText primary={err} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                ) : (
                                <Box sx={{ pt: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                        <FormControl sx={{ minWidth: 200, maxWidth: 300 }}>
                                            <InputLabel>Category</InputLabel>
                                            <Select
                                                size="small"
                                                value={selectedCategory}
                                                onChange={(e) => this.setState({ selectedCategory: e.target.value })}
                                                label="Category"
                                                sx={{ mb: 0, "& .MuiSelect-select": { paddingTop: "8px", paddingBottom: "8px" } }}
                                            >
                                                {categoryOptions.map(opt => (
                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl sx={{ minWidth: 180 }}>
                                            <InputLabel>Source</InputLabel>
                                            <Select
                                                size="small"
                                                value={this.state.selectedJsonVersion || ""}
                                                label="Source"
                                                onChange={e => {
                                                    const newVersion = e.target.value;
                                                    this.setState({ selectedJsonVersion: newVersion });
                                                }}
                                            >
                                                {loadedVersions.map(v => (
                                                    <MenuItem key={v.version} value={v.version}>
                                                        {{
                                                            "Production": "Prod:",
                                                            "Beta": "Beta:",
                                                            "Internal": "Int:",
                                                            "Development": "Dev:"
                                                        }[v.branch ?? ""]} {v.version}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl sx={{ minWidth: 180 }}>
                                            <InputLabel>Diff</InputLabel>
                                            <Select
                                                size="small"
                                                value={this.state.diffJsonVersion || "None"}
                                                label="Diff"
                                                onChange={e => {
                                                    this.setState({ diffJsonVersion: e.target.value });
                                                }}
                                            >
                                                <MenuItem value="None">None</MenuItem>
                                                {loadedVersions.map(v => (
                                                    <MenuItem key={v.version} value={v.version}>
                                                        {{
                                                            "Production": "Prod:",
                                                            "Beta": "Beta:",
                                                            "Internal": "Int:",
                                                            "Development": "Dev:"
                                                        }[v.branch ?? ""]} {v.version}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                const { major, minor } = globals.decompactVersion(this.state.selectedVersion);
                                                const selectedPatch = this.state.selectedJsonVersion
                                                    ? parseInt(this.state.selectedJsonVersion.split(".")[2])
                                                    : 0;
                                                this.setState({
                                                    loadDialogOpen: true,
                                                    loadBranch: this.state.selectedBranch,
                                                    loadMajor: major,
                                                    loadMinor: minor,
                                                    loadPatch: selectedPatch
                                                });
                                            }}
                                        >
                                            Load Version
                                        </Button>
                                    </Box>
                                    {this.state.loadingJsonVersion && (
                                        <Typography variant="body2" sx={{ mb: 2 }}>Loading...</Typography>
                                    )}
                                    {showDiff ? (
                                        <Box sx={{ mt: 2 }}>
                                            {diffResult.length === 0 ? (
                                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                                    Versions have the same content.{'\n'}No changes detected.
                                                </Typography>
                                            ) : (
                                                <ul>
                                                    {diffResult
                                                        .filter(change => (
                                                            selectedCategory === "All" ||
                                                            (change.path && change.path[0] === selectedCategory)
                                                        ))
                                                        .map((change, i) => {
                                                            const displayPath = (() => {
                                                                const path = change.path ?? [];
                                                                return (selectedCategory !== "All" && path[0] === selectedCategory)
                                                                    ? path.slice(1).join('.')
                                                                    : path.join('.');
                                                            })();
                                                            switch (change.kind) {
                                                                case 'N':
                                                                    return <li key={i}>🟢 Added <b>{displayPath}</b>: {JSON.stringify(change.rhs)}</li>;
                                                                case 'D':
                                                                    return <li key={i}>🔴 Removed <b>{displayPath}</b>: {JSON.stringify(change.lhs)}</li>;
                                                                case 'E':
                                                                    return <li key={i}>🟡 Changed <b>{displayPath}</b>: {JSON.stringify(change.lhs)} → {JSON.stringify(change.rhs)}</li>;
                                                                case 'A':
                                                                    return <li key={i}>🔵 Array Change at <b>{displayPath}</b>[{change.index}]: {JSON.stringify(change.item)}</li>;
                                                                default:
                                                                    return null;
                                                            }
                                                        })}
                                                </ul>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box sx={{ height: 400 }}>
                                            <DataGrid
                                                rows={tableRows}
                                                columns={tableColumns}
                                                disableSelectionOnClick
                                                autoHeight
                                            />
                                        </Box>
                                    )}
                                </Box>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button
                                  onClick={() => {
                                    if (!this.state.inspectorReadOnly && this.state.downloadTempId) {
                                        this.performAction(
                                            "Cancel Docs",
                                            "cancel-docs",
                                            this.state.downloadTempId.toString()
                                        );
                                    }
                                    this.setState({ showDownloadDialog: false });
                                  }}
                                >
                                  {this.state.inspectorReadOnly ? "Close" : "Cancel"}
                                </Button>
                                {!this.state.inspectorReadOnly && (
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            this.performAction(
                                                "Apply Docs",
                                                "apply-docs",
                                                this.state.selectedVersionId,
                                                this.state.downloadTempId
                                            );
                                            this.setState({ showDownloadDialog: false });
                                        }}
                                    >
                                        Deploy
                                    </Button>
                                )}
                            </DialogActions>
                            {/* New Load Version Dialog */}
                            <Dialog open={this.state.loadDialogOpen} onClose={() => this.setState({ loadDialogOpen: false })}>
                                <DialogTitle>Load Game DB Version</DialogTitle>
                                <DialogContent>
                                    <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                                        <FormControl sx={{ minWidth: 120 }}>
                                            <InputLabel>Branch</InputLabel>
                                            <Select
                                                size="small"
                                                value={this.state.loadBranch}
                                                label="Branch"
                                                onChange={e => this.setState({ loadBranch: e.target.value })}
                                            >
                                                {globals.branches.map(b => (
                                                    <MenuItem key={b} value={b}>{b}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl sx={{ minWidth: 120 }}>
                                            <InputLabel>Version</InputLabel>
                                            <Select
                                                size="small"
                                                value={`${this.state.loadMajor}.${this.state.loadMinor}`}
                                                label="Version"
                                                onChange={e => {
                                                    const [major, minor] = e.target.value.split(".").map(Number);
                                                    this.setState({ loadMajor: major, loadMinor: minor, loadPatch: 0 });
                                                }}
                                            >
                                                {(() => {
                                                    const branchIndex = globals.branches.indexOf(this.state.loadBranch);
                                                    const branchData = this.state.versionData.find(d => d.branch === branchIndex);
                                                const versions = [...new Set((branchData?.versions ?? []).map(v => {
                                                    const { major, minor } = globals.decompactVersion(v.version);
                                                    return `${major}.${minor}`;
                                                }))];
                                                    return versions.map(ver => (
                                                        <MenuItem key={ver} value={ver}>{ver}</MenuItem>
                                                    ));
                                                })()}
                                            </Select>
                                        </FormControl>
                                        <FormControl sx={{ minWidth: 120 }}>
                                            <InputLabel>Patch</InputLabel>
                                            <Select
                                                size="small"
                                                value={this.state.loadPatch}
                                                label="Patch"
                                                onChange={e => this.setState({ loadPatch: Number(e.target.value) })}
                                            >
                                                {(() => {
                                                    const branchIndex = globals.branches.indexOf(this.state.loadBranch);
                                                    const branchData = this.state.versionData.find(d => d.branch === branchIndex);
                                                    // Find all versions for this branch that match major/minor
                                                    const versions = (branchData?.versions ?? []).filter(v => {
                                                        const { major, minor } = globals.decompactVersion(v.version);
                                                        return major === this.state.loadMajor && minor === this.state.loadMinor;
                                                    });
                                                    // Collect all patches for this version (gameDb or metaDb)
                                                    const patches = versions.flatMap(v =>
                                                        this.state.downloadSource === "metaDb"
                                                            ? v.metaDb.map(d => d.patch)
                                                            : v.gameDb.map(d => d.patch)
                                                    );
                                                    const uniquePatches = [...new Set(patches)];
                                                    return uniquePatches.map(patch => (
                                                        <MenuItem key={patch} value={patch}>{patch}</MenuItem>
                                                    ));
                                                })()}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => this.setState({ loadDialogOpen: false })}>Cancel</Button>
                                    <Button
                                        variant="contained"
                                        onClick={async () => {
                                            const { loadMajor, loadMinor, loadPatch, loadBranch } = this.state;
                                            const branchIndex = globals.branches.indexOf(loadBranch);
                                            const compact = globals.compactVersion(loadMajor, loadMinor, loadPatch, branchIndex);
                                            const versionStr = this.versionString(loadMajor, loadMinor, loadPatch);
                                            const list = this.state.downloadSource === "metaDb"
                                                ? this.state.loadedMetaDbVersions
                                                : this.state.loadedGameDbVersions;
                                            if (list.some(v => v.version === versionStr)) {
                                                enqueueSnackbar(`Version ${versionStr} already loaded.`, { variant: "info" });
                                                this.setState({ loadDialogOpen: false });
                                                return;
                                            }
                                            this.setState({ loadingJsonVersion: true, loadDialogOpen: false });
                                            try {
                                                if (this.state.downloadSource === "metaDb") {
                                                    const result = await this.performRequest("Fetch Meta DB", "get-docs", "meta", compact);
                                                    this.addLoadedMetaDbVersion(versionStr, JSON.stringify(result.data), loadBranch);
                                                } else {
                                                    const result = await this.performRequest("Fetch Game DB", "get-docs", "game", compact);
                                                    this.addLoadedGameDbVersion(versionStr, result.data.json, loadBranch);
                                                }
                                            } catch {}
                                            this.setState({
                                                loadingJsonVersion: false,
                                                diffJsonVersion: versionStr
                                            });
                                        }}
                                    >
                                        Load
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Dialog>
                    </>
                )}
            </Box>
        );
    }
}